import * as borogove from "borogove";
import Delta from "quill-delta";
import { href as rhref } from "react-router";
import { useRef, useCallback, useEffect, useSyncExternalStore } from "react";

export const ios = navigator.userAgent.match(/(iPad|iPhone|iPod)/g);
export const webkit =
	navigator.userAgent.match(/AppleWebKit/) &&
	!navigator.userAgent.match(/Chrom/);

export const href = (
	template: string,
	params: Record<string, string>,
	query?: URLSearchParams,
) => {
	const escapedParams: Record<string, string> = {};
	for (const [k, v] of Object.entries(params)) {
		escapedParams[k] = encodeURIComponent(v).replace(/%40/, "@");
	}
	const url = rhref(template, escapedParams);
	return url + (query?.size ? "?" + query : "");
};

export function useEffectEvent<Args extends unknown[], R>(
	fn: (...args: Args) => R,
): (...args: Args) => R {
	const ref = useRef(fn);

	// Keep the latest function in ref
	useEffect(() => {
		ref.current = fn;
	});

	// Return a stable function that always calls the latest version
	return useCallback((...args: Args) => {
		return ref.current(...args);
	}, []);
}

export type Attachment = { attachment: borogove.ChatAttachment; uri: string };
export type ChatExtras = {
	draftText?: string;
	draftDelta?: Delta;
	attachments: Attachment[];
	currentThread?: string;
	scrollTo?: string;
	tabIndex: number;
	tabNames: string[];
	commandSessions: Promise<borogove.CommandSession>[];
	commandLog: { id: string; form: borogove.Form; data: FormData }[];
};

const emptyExtra = (v: ChatExtras[keyof ChatExtras]) => {
	if (v === undefined) return true;
	if (typeof v === "string") return v.length < 1;
	if (v instanceof Array) return v.length < 1;
	return true; // Prentend the Delta is empty so if text is we can delete
};

// We never show this while it is changing so it doesn't need to be reactive
export const useChatExtras = (accountId: string) => {
	const extras = useRef<Record<string, ChatExtras | undefined>>({});

	return {
		getChatExtras: (id: string) => extras.current[`${accountId}\x00${id}`],
		setChatExtras: (id: string, update: Partial<ChatExtras>) => {
			const fullId = `${accountId}\x00${id}`;
			extras.current[fullId] = {
				attachments: [],
				tabIndex: 0,
				tabNames: [],
				commandSessions: [],
				commandLog: [],
				...extras.current[fullId],
				...update,
			};
			if (Object.values(extras.current[fullId]).every(emptyExtra)) {
				delete extras.current[fullId];
			}
		},
	};
};

export const showNotification = async (
	accountId: string,
	chat: borogove.Chat,
	message: borogove.ChatMessage,
) => {
	if (!Notification?.permission) return;

	const tag = accountId + ">" + message.chatId();
	if (navigator.serviceWorker) {
		// Check if we already notified this message
		const serviceWorkerRegistration = await navigator.serviceWorker.ready;
		const notifs = await serviceWorkerRegistration.getNotifications({
			tag: tag,
		});
		for (const notif of notifs) {
			if (notif.data.messageId === (message.serverId || message.localId))
				return;
		}
	}

	const participant = chat.getParticipantDetails(message.senderId);
	const title =
		"Message from " + (participant?.displayName ?? message.chatId());
	let icon = participant.photoUri ?? participant.placeholderUri;
	if (!icon.startsWith("data:")) {
		const response = await fetch(icon);
		const mime = response.headers.get("Content-Type");
		if (response.ok && mime) {
			const buffer = await response.arrayBuffer();
			const base64 = btoa(String.fromCharCode(...new Uint8Array(buffer)));
			icon = `data:${mime};base64,${base64}`;
		}
	}
	const options = {
		body: message.text ?? undefined,
		icon,
		tag,
		renotify: true,
		timestamp: new Date(message.timestamp).getTime(),
		vibrate: [50, 50, 50],
		data: {
			accountId,
			chatId: message.chatId(),
			messageId: message.serverId || message.localId,
			timestamp: message.timestamp,
		},
	};
	if (navigator.serviceWorker) {
		const serviceWorkerRegistration = await navigator.serviceWorker.ready;
		await serviceWorkerRegistration.showNotification(title, options);
	} else {
		const notif = new Notification(title, options);
		notif.addEventListener("click", () => {
			history.pushState(
				null,
				"",
				href("/accounts/:accountId/chats/:chatId", {
					accountId: accountId,
					chatId: message.chatId(),
				}) + `#message:${message.serverId || message.localId}`,
			);
			window.dispatchEvent(new PopStateEvent("popstate")); // Notify React Router
		});
		return notif;
	}
};

const negatedScriptRegex = {
	Latn: /(?=\p{L})\P{Script=Latin}/u,
	Cyrl: /(?=\p{L})\P{Script=Cyrillic}/u,
	Arab: /(?=\p{L})\P{Script=Arabic}/u,
	Hans: /(?=\p{L})\P{Script=Han}/u,
	Hant: /(?=\p{L})\P{Script=Han}/u,
	Jpan: /(?=\p{L})[^\p{Script=Han}\p{Script=Hiragana}\p{Script=Katakana}]/u,
	Kore: /(?=\p{L})[^\p{Script=Hangul}\p{Script=Han}]/u,
	Deva: /(?=\p{L})\P{Script=Devanagari}/u,
	Guru: /(?=\p{L})\P{Script=Gurmukhi}/u,
	Ethi: /(?=\p{L})\P{Script=Ethiopic}/u,
	Hebr: /(?=\p{L})\P{Script=Hebrew}/u,
	Grek: /(?=\p{L})\P{Script=Greek}/u,
};

export const badScript = (s: string) => {
	for (const lang of navigator.languages) {
		const script = (new Intl.Locale(lang).maximize().script ??
			"Latn") as keyof typeof negatedScriptRegex;
		if (!negatedScriptRegex[script].test(s)) return false;
	}

	return true;
};

export const isInvite = (messageRequests: string, c: borogove.Chat) => {
	if (c.uiState === borogove.UiState.Invited) return true;
	if (messageRequests === "strangers") return !c.isTrusted();
	if (messageRequests === "spam" && c.lastMessage?.text) {
		if (c.isTrusted() || c.isBookmarked) return false;
		return badScript(c.lastMessage.text);
	}
	return false;
};

export const useChats = (
	client: borogove.Client | undefined,
): borogove.Chat[] => {
	const chatProxies = useRef(new WeakMap()).current;
	const chatsRef = useRef<borogove.Chat[] | null>(client?.getChats() || []);

	useEffect(() => {
		chatsRef.current = client ? null : [];
	}, [client]);

	return useSyncExternalStore(
		(callback) => {
			const token = client?.addChatsUpdatedListener((updatedChats) => {
console.log("update");
				for (const chat of updatedChats) {
					chatProxies.delete(chat);
				}

				chatsRef.current = null;
console.log("updated");
				callback();
console.log("updated2");
			});

			return () => {
				if (client && token) client.removeEventListener(token);
			};
		},
		() => {
			if (chatsRef.current != null) return chatsRef.current;

			return (chatsRef.current = (client?.getChats() || []).map((chat) => {
				if (!chatProxies.has(chat)) {
					chatProxies.set(chat, new Proxy(chat, {}));
				}
				return chatProxies.get(chat);
			}));
		},
	);
};
